import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { LANDING_TEMPLATES, LandingTemplateId } from '../../data/landingTemplates';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [template, setTemplate] = useState<LandingTemplateId>('caso_exito');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    const landing = await createLanding(organizationId, name.trim(), formId || undefined, template);
    setSaving(false);
    setCreating(false);
    setName('');
    setFormId('');
    setTemplate('caso_exito');
    navigate(`/app/landings/${landing.id}/editor`);
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1059" data-mp="2h40f6kckt2v" />} onClick={() => setCreating(true)} data-id="element-1058" data-mp="7hrrttchnbxm">
            Nueva landing
          </Button>} data-id="element-1057" data-mp="26mjfhyinsgqp" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-1061" data-mp="geiuioljpp16" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-1062" data-mp="3jxlivhqssegg">Crear landing</Button>} data-id="element-1060" data-mp="gg3dri2etljq" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1063" data-mp="3fwrvcph9p1o3">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1064" data-mp="314hofu5r9xks">
                <div className="flex items-start justify-between gap-3" data-id="element-1065" data-mp="1bp6ytpji12rg">
                  <div className="min-w-0" data-id="element-1066" data-mp="1bnvthlaenmy8">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1067" data-mp="1ghgwjrquv74a">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-1068" data-mp="17on22n2u815u">
                      /landing/{landing.slug}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1070" data-mp="1zerm2kmoqm6j">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1071" data-mp="1qcs1xjgdj1zd" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1072" data-mp="f096sf34vixz" />,
              onSelect: () => window.open(`/landing/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-1073" data-mp="phl2cwqlkbqd" />,
              onSelect: () => navigator.clipboard?.writeText(`${window.location.origin}/landing/${landing.slug}`)
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1074" data-mp="gjj2wlmihr57" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Despublicar' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1075" data-mp="194967ywf5v0g" />,
              onSelect: () => setLandingStatus(landing.id, landing.status === 'publicada' ? 'despublicada' : 'publicada')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1076" data-mp="3oy15i7tooehf" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-1069" data-mp="1zfkvjtd13psd" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1077" data-mp="39uhxgulboln2">
                  <div data-id="element-1078" data-mp="39wb5oebl3dx6">
                    <dt className="text-xs text-muted-foreground" data-id="element-1079" data-mp="39sncxhdyqvji">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1080" data-mp="3etuhl7kt7ely">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-1081" data-mp="3jqtbpq4h3lk5">
                    <dt className="text-xs text-muted-foreground" data-id="element-1082" data-mp="3oj4dkyirxhfc">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1083" data-mp="3ol7dca9qaox9">{landing.views}</dd>
                  </div>
                  <div data-id="element-1084" data-mp="1eoem93iqy2u">
                    <dt className="text-xs text-muted-foreground" data-id="element-1085" data-mp="1dvis363lszh">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-1086" data-mp="6e12mx64mb70">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-1087" data-mp="barra8vzyf41">
                    <dt className="text-xs text-muted-foreground" data-id="element-1088" data-mp="285e4tegpm4zd">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-1089" data-mp="287h46p3r5agr">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-1090" data-mp="252q4vt5jzigj">
                  <Tag data-id="element-1091" data-mp="204ye52gna2dx">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-1092" data-mp="206j92t6vfncb">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-1094" data-mp="2a4yz0su5wqk8">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1095" data-mp="2o1c9hi0a3rp7">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim()} data-id="element-1096" data-mp="2j2rga2jg28a9">
              Crear
            </Button>
          </div>} data-id="element-1093" data-mp="2a2mafwtwfhy7">
        <div className="space-y-5" data-id="element-1097" data-mp="2st3xbks1vjbn">
          <fieldset data-id="element-1098" data-mp="2su5d22k0gtzp">
            <legend className="mb-2 text-sm font-medium text-foreground" data-id="element-1099" data-mp="2stm1d3kyf1u4">
              Plantilla de partida
            </legend>
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-1100" data-mp="36qiwhh83v5gt">
              {LANDING_TEMPLATES.map(option => {
              const active = template === option.id;
              return <button key={option.id} type="button" onClick={() => setTemplate(option.id)} aria-pressed={active} className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-id="element-1101" data-mp="3lg3o2qrixd3q">
                    <span className="block text-sm font-semibold text-foreground" data-id="element-1102" data-mp="3hd7bphskud88">
                      {option.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground" data-id="element-1103" data-mp="32mzsgzuqwq6h">
                      {option.description}
                    </span>
                  </button>;
            })}
            </div>
          </fieldset>
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-1104" data-mp="2xphug4i9nvd9" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1105" data-mp="2jsbc2b1gfyok" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-1106" data-mp="2z9jauppskevg" />
    </>;
}